/** CSV modal — saved templates (API). For Media Devoted | Created By Ciro Autiero */
import { useCallback, useState } from 'react';

import type { CsvExportTemplate } from '../../useCsvExportTemplates';
import {
  useCreateCsvExportTemplate,
  useCsvExportTemplatesForTab,
  useDeleteCsvExportTemplate,
  useUpdateCsvExportTemplate,
} from '../api';
import { useCsvExportModalOpenSync } from '../useCsvExportModalOpenSync';
import type { UseCsvExportModalTemplatesParams } from './useCsvExportModalTemplates.types';

export function useCsvExportModalTemplates(p: UseCsvExportModalTemplatesParams) {
  const { data: remoteTemplates = [], isFetched } = useCsvExportTemplatesForTab(
    p.userId,
    p.tab,
    p.open,
  );
  const createMut = useCreateCsvExportTemplate();
  const updateMut = useUpdateCsvExportTemplate();
  const deleteMut = useDeleteCsvExportTemplate();

  const [templateName, setTemplateName] = useState('');
  const [templateColor, setTemplateColor] = useState('#3b82f6');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);

  const templatesBusy =
    createMut.isPending || updateMut.isPending || deleteMut.isPending;

  useCsvExportModalOpenSync(
    p.open,
    p.tab,
    p.columnKeySig,
    p.columns,
    remoteTemplates,
    isFetched,
    p.setExportColumnKeys,
    p.setExportDownloadBaseName,
    p.exportDownloadFilenameTouched,
    setTemplateName,
    setTemplateColor,
    setSaveAsDefault,
    setEditingTemplateId,
  );

  const applyTemplate = useCallback(
    (t: CsvExportTemplate) => {
      p.setExportDownloadFilenameTouched(false);
      const allow = new Set(p.allColumnKeys);
      const exact = (t.columns ?? []).filter(
        (k): k is string => typeof k === 'string' && allow.has(k),
      );
      p.setExportColumnKeys(exact);
      p.setExportDownloadBaseName(
        typeof t.downloadBaseName === 'string' ? t.downloadBaseName : '',
      );
      setTemplateName(t.name);
      setTemplateColor(t.color);
      setSaveAsDefault(t.isDefault);
      setEditingTemplateId(t.id);
    },
    [
      p.allColumnKeys,
      p.setExportDownloadFilenameTouched,
      p.setExportColumnKeys,
      p.setExportDownloadBaseName,
    ],
  );

  const deleteTemplate = useCallback(
    (t: CsvExportTemplate): boolean => {
      if (!p.canManageTemplates || templatesBusy) return false;
      if (!window.confirm(`Delete template “${t.name}”?`)) return false;
      void deleteMut.mutateAsync(t.id);
      return true;
    },
    [p.canManageTemplates, templatesBusy, deleteMut],
  );

  const handleSaveTemplate = useCallback(() => {
    if (!p.canManageTemplates || templatesBusy) return;
    const name = templateName.trim();
    if (!name) return;
    const trimmedFile = p.exportDownloadBaseName.trim();
    void createMut
      .mutateAsync({
        tab: p.tab,
        name,
        color: templateColor,
        columns: [...p.exportColumnKeys],
        isDefault: saveAsDefault,
        ...(trimmedFile ? { downloadBaseName: trimmedFile } : {}),
      })
      .then(() => {
        setTemplateName('');
        setEditingTemplateId(null);
      })
      .catch(() => {});
  }, [
    p.canManageTemplates,
    p.tab,
    p.exportColumnKeys,
    p.exportDownloadBaseName,
    templatesBusy,
    templateName,
    templateColor,
    saveAsDefault,
    createMut,
  ]);

  const handleUpdateTemplate = useCallback(() => {
    if (!p.canManageTemplates || templatesBusy || !editingTemplateId) return;
    const name = templateName.trim();
    if (!name) return;
    const trimmedFile = p.exportDownloadBaseName.trim();
    void updateMut
      .mutateAsync({
        id: editingTemplateId,
        name,
        color: templateColor,
        columns: [...p.exportColumnKeys],
        isDefault: saveAsDefault,
        ...(trimmedFile ? { downloadBaseName: trimmedFile } : { downloadBaseName: '' }),
      })
      .catch(() => {});
  }, [
    p.canManageTemplates,
    templatesBusy,
    editingTemplateId,
    templateName,
    templateColor,
    p.exportColumnKeys,
    p.exportDownloadBaseName,
    saveAsDefault,
    updateMut,
  ]);

  return {
    templates: remoteTemplates,
    templatesBusy,
    templateName,
    setTemplateName,
    templateColor,
    setTemplateColor,
    saveAsDefault,
    setSaveAsDefault,
    editingTemplateId,
    applyTemplate,
    deleteTemplate,
    handleSaveTemplate,
    handleUpdateTemplate,
  };
}
